import { useState, useEffect, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate, useParams, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Menu, X, ChevronRight, Plus, Edit2, Trash2, LogOut, Package, Grid3X3, FileText, Settings, Eye, ShoppingBag, MessageCircle, Phone, ChevronLeft, ZoomIn, ImageIcon, Star, Quote, CheckCircle, XCircle, MapPin, Clock, Calendar, User, BookOpen } from "lucide-react";
import "./App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============ SCROLL TO TOP COMPONENT ============
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// ============ SEO COMPONENT ============
const SEO = ({ title, description }) => {
  useEffect(() => {
    // Update document title
    document.title = `${title} | LT Cards & Creation`;
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    }
  }, [title, description]);
  
  return null;
};

// ============ LIGHTBOX COMPONENT ============
const Lightbox = ({ images, currentIndex, onClose, onPrev, onNext }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [onClose, onPrev, onNext]);

  return (
    <div className="lightbox-overlay" onClick={onClose} data-testid="lightbox-overlay">
      <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button 
          onClick={onClose} 
          className="lightbox-close"
          data-testid="lightbox-close"
        >
          <X size={28} />
        </button>
        
        {/* Navigation arrows */}
        {images.length > 1 && (
          <>
            <button 
              onClick={onPrev} 
              className="lightbox-nav lightbox-prev"
              data-testid="lightbox-prev"
            >
              <ChevronLeft size={32} />
            </button>
            <button 
              onClick={onNext} 
              className="lightbox-nav lightbox-next"
              data-testid="lightbox-next"
            >
              <ChevronRight size={32} />
            </button>
          </>
        )}
        
        {/* Main image */}
        <div className="lightbox-image-container">
          <img 
            src={images[currentIndex]} 
            alt={`Gallery image ${currentIndex + 1}`}
            className="lightbox-image"
          />
        </div>
        
        {/* Image counter */}
        <div className="lightbox-counter">
          {currentIndex + 1} / {images.length}
        </div>
        
        {/* Thumbnails */}
        {images.length > 1 && (
          <div className="lightbox-thumbnails">
            {images.map((img, idx) => (
              <button
                key={idx}
                onClick={() => onNext(idx)}
                className={`lightbox-thumb ${idx === currentIndex ? 'active' : ''}`}
              >
                <img src={img} alt={`Thumbnail ${idx + 1}`} />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============ PRODUCT GALLERY COMPONENT ============
const ProductGallery = ({ images, productName }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  
  // Ensure we always have at least one image
  const galleryImages = images && images.length > 0 
    ? images 
    : ['https://images.pexels.com/photos/7405670/pexels-photo-7405670.jpeg'];
  
  const openLightbox = (index) => {
    setSelectedIndex(index);
    setLightboxOpen(true);
  };
  
  const closeLightbox = () => setLightboxOpen(false);
  
  const goToPrev = () => {
    setSelectedIndex((prev) => (prev === 0 ? galleryImages.length - 1 : prev - 1));
  };
  
  const goToNext = (specificIndex) => {
    if (typeof specificIndex === 'number') {
      setSelectedIndex(specificIndex);
    } else {
      setSelectedIndex((prev) => (prev === galleryImages.length - 1 ? 0 : prev + 1));
    }
  };

  return (
    <div className="product-gallery">
      {/* Main Image */}
      <div 
        className="gallery-main-image group cursor-pointer"
        onClick={() => openLightbox(selectedIndex)}
        data-testid="gallery-main-image"
      >
        <img 
          src={galleryImages[selectedIndex]} 
          alt={productName}
          className="w-full h-full object-cover"
        />
        <div className="gallery-zoom-hint">
          <ZoomIn size={24} />
          <span>Click to zoom</span>
        </div>
      </div>
      
      {/* Thumbnails */}
      {galleryImages.length > 1 && (
        <div className="gallery-thumbnails">
          {galleryImages.map((img, idx) => (
            <button
              key={idx}
              onClick={() => setSelectedIndex(idx)}
              className={`gallery-thumb ${idx === selectedIndex ? 'active' : ''}`}
              data-testid={`gallery-thumb-${idx}`}
            >
              <img src={img} alt={`${productName} view ${idx + 1}`} />
            </button>
          ))}
        </div>
      )}
      
      {/* Lightbox */}
      {lightboxOpen && (
        <Lightbox 
          images={galleryImages}
          currentIndex={selectedIndex}
          onClose={closeLightbox}
          onPrev={goToPrev}
          onNext={goToNext}
        />
      )}
    </div>
  );
};

// Settings Context for WhatsApp
const SettingsContext = createContext(null);

const useSettings = () => useContext(SettingsContext);

const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({ whatsapp_number: '', whatsapp_message: '' });
  
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings`);
        setSettings(res.data);
      } catch (e) {
        console.error("Error fetching settings:", e);
      }
    };
    fetchSettings();
  }, []);
  
  const refreshSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setSettings(res.data);
    } catch (e) {
      console.error("Error refreshing settings:", e);
    }
  };
  
  return (
    <SettingsContext.Provider value={{ settings, refreshSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem("admin_token"));
  const [username, setUsername] = useState(localStorage.getItem("admin_username"));

  const login = (newToken, newUsername) => {
    localStorage.setItem("admin_token", newToken);
    localStorage.setItem("admin_username", newUsername);
    setToken(newToken);
    setUsername(newUsername);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (e) {
      console.error("Logout error:", e);
    }
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_username");
    setToken(null);
    setUsername(null);
  };

  return (
    <AuthContext.Provider value={{ token, username, login, logout, isAuthenticated: !!token }}>
      {children}
    </AuthContext.Provider>
  );
};

// API helper with auth
const useApi = () => {
  const { token } = useAuth();
  
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
  
  return {
    get: (url, requiresAuth = false) => requiresAuth 
      ? axios.get(`${API}${url}`, { headers: authHeaders })
      : axios.get(`${API}${url}`),
    post: (url, data) => axios.post(`${API}${url}`, data, { headers: authHeaders }),
    put: (url, data) => axios.put(`${API}${url}`, data, { headers: authHeaders }),
    delete: (url) => axios.delete(`${API}${url}`, { headers: authHeaders }),
  };
};

// ============ STOREFRONT COMPONENTS ============

const Header = ({ categories }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  return (
    <header className="sticky top-0 z-50 glass border-b border-[#E5E0D8]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="font-playfair text-2xl font-bold text-[#4A0404]" data-testid="logo-link">
            LT Cards & Creation
          </Link>
          
          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={cat.is_page ? `/about` : `/category/${cat.slug}`}
                className="font-manrope text-sm font-medium text-[#595959] hover:text-[#4A0404] transition-colors whitespace-nowrap"
                data-testid={`nav-${cat.slug}`}
              >
                {cat.name}
              </Link>
            ))}
          </nav>
          
          <div className="hidden md:flex items-center space-x-4">
            <Link 
              to="/admin" 
              className="font-manrope text-sm font-medium text-[#595959] hover:text-[#4A0404]"
              data-testid="admin-link"
            >
              Admin
            </Link>
          </div>
          
          {/* Mobile menu button */}
          <button 
            className="md:hidden p-2" 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        
        {/* Mobile Nav */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-[#E5E0D8] animate-slideIn">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={cat.is_page ? `/about` : `/category/${cat.slug}`}
                className="block py-3 font-manrope text-[#595959] hover:text-[#4A0404]"
                onClick={() => setIsMenuOpen(false)}
                data-testid={`mobile-nav-${cat.slug}`}
              >
                {cat.name}
              </Link>
            ))}
            <Link 
              to="/admin" 
              className="block py-3 font-manrope text-[#595959] hover:text-[#4A0404]"
              onClick={() => setIsMenuOpen(false)}
            >
              Admin
            </Link>
          </div>
        )}
      </div>
    </header>
  );
};

const CategoryTabs = ({ categories, activeSlug }) => {
  return (
    <div className="sticky top-20 z-40 bg-white border-b border-[#E5E0D8]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex space-x-1 overflow-x-auto scrollbar-hide py-1">
          {categories.filter(c => !c.is_page).map((cat) => (
            <Link
              key={cat.id}
              to={`/category/${cat.slug}`}
              className={`whitespace-nowrap px-6 py-3 font-playfair text-lg font-medium transition-colors border-b-2 ${
                activeSlug === cat.slug 
                  ? 'border-[#4A0404] text-[#4A0404]' 
                  : 'border-transparent text-[#595959] hover:text-[#4A0404]'
              }`}
              data-testid={`tab-${cat.slug}`}
            >
              {cat.name}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

const HeroSection = () => {
  return (
    <section className="hero-section">
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(https://images.pexels.com/photos/30921929/pexels-photo-30921929.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260)` }}
      />
      <div className="hero-overlay" />
      <div className="hero-content">
        <p className="font-cormorant text-lg md:text-xl text-[#D4AF37] italic mb-4 animate-fadeInUp opacity-0" style={{ animationDelay: '0.1s', animationFillMode: 'forwards' }}>
          Welcome to LT Cards & Creation
        </p>
        <h1 className="font-playfair text-4xl md:text-6xl lg:text-7xl text-white font-bold mb-6 animate-fadeInUp opacity-0" style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}>
          <span className="italic">Crafting</span> Memories,<br />
          <span className="text-[#D4AF37]">Forever</span>
        </h1>
        <div className="gold-line animate-fadeInUp opacity-0" style={{ animationDelay: '0.5s', animationFillMode: 'forwards' }} />
        <p className="font-manrope text-lg md:text-xl text-white/80 mb-10 max-w-2xl mx-auto animate-fadeInUp opacity-0" style={{ animationDelay: '0.6s', animationFillMode: 'forwards' }}>
          Premium printing solutions for your special occasions. From elegant invitations to luxurious packaging.
        </p>
        <div className="flex flex-col sm:flex-row gap-6 justify-center items-center animate-fadeInUp opacity-0" style={{ animationDelay: '0.8s', animationFillMode: 'forwards' }}>
          <Link to="/category/invitations" className="btn-primary inline-flex items-center justify-center px-8 py-4 min-w-[200px]" data-testid="hero-cta">
            Explore Collection
            <ChevronRight size={18} className="ml-2" />
          </Link>
          <Link to="/about" className="btn-outline border-white text-white hover:bg-white hover:text-[#4A0404] inline-flex items-center justify-center px-8 py-4 min-w-[160px]">
            Our Story
          </Link>
        </div>
      </div>
      
      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-float">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
          <div className="w-1 h-2 bg-white/50 rounded-full animate-pulse" />
        </div>
      </div>
    </section>
  );
};

const CategoryCard = ({ category, index }) => {
  const navigate = useNavigate();
  
  return (
    <div 
      className="category-card animate-fadeInUp opacity-0"
      style={{ animationDelay: `${0.1 * (index + 1)}s`, animationFillMode: 'forwards' }}
      onClick={() => navigate(category.is_page ? '/about' : `/category/${category.slug}`)}
      data-testid={`category-card-${category.slug}`}
    >
      <div className="overflow-hidden h-3/4">
        <img 
          src={category.image_url || 'https://images.pexels.com/photos/7405670/pexels-photo-7405670.jpeg'} 
          alt={category.name}
          className="category-card-image"
        />
      </div>
      <div className="category-card-content">
        <h3 className="font-playfair text-xl font-semibold text-[#1A1A1A] mb-1">{category.name}</h3>
        <span className="inline-flex items-center text-[#D4AF37] font-manrope text-sm">
          View Collection <ChevronRight size={14} className="ml-1" />
        </span>
      </div>
    </div>
  );
};

const ProductCard = ({ product }) => {
  return (
    <Link 
      to={`/product/${product.slug}`} 
      className="product-showcase-card group"
      data-testid={`product-card-${product.slug}`}
    >
      <div className="relative overflow-hidden">
        <img 
          src={product.image_url || 'https://images.pexels.com/photos/7405670/pexels-photo-7405670.jpeg'} 
          alt={product.name}
          className="product-showcase-image"
        />
        <div className="product-showcase-overlay" />
        <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="text-white font-manrope text-sm bg-[#4A0404] px-4 py-2 rounded-full">
            View Details
          </span>
        </div>
      </div>
      <div className="p-5">
        <h3 className="font-playfair text-lg font-semibold text-[#1A1A1A] group-hover:text-[#4A0404] transition-colors">
          {product.name}
        </h3>
        {product.description && (
          <p className="font-manrope text-sm text-[#595959] mt-2 line-clamp-2">{product.description}</p>
        )}
        <div className="flex items-center justify-between mt-4">
          <p className="font-playfair text-xl font-bold text-[#4A0404]">₹{product.price.toLocaleString()}</p>
          <span className="text-[#D4AF37]">
            <ChevronRight size={20} />
          </span>
        </div>
      </div>
    </Link>
  );
};

// Testimonial Component
const TestimonialCard = ({ name, text, rating = 5 }) => {
  return (
    <div className="testimonial-card">
      <div className="testimonial-stars flex gap-1 mb-4">
        {[...Array(rating)].map((_, i) => (
          <svg key={i} className="w-5 h-5 fill-current" viewBox="0 0 20 20">
            <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
          </svg>
        ))}
      </div>
      <p className="font-cormorant text-lg italic text-[#595959] mb-4">"{text}"</p>
      <p className="font-manrope font-semibold text-[#1A1A1A]">{name}</p>
    </div>
  );
};

// Testimonials Slider with Navigation
const TestimonialsSlider = ({ testimonials }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const itemsPerPage = window.innerWidth >= 768 ? 3 : 1;
  const totalPages = Math.ceil(testimonials.length / itemsPerPage);
  
  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % totalPages);
  };
  
  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + totalPages) % totalPages);
  };
  
  const visibleTestimonials = testimonials.slice(
    currentIndex * itemsPerPage,
    currentIndex * itemsPerPage + itemsPerPage
  );
  
  return (
    <div className="relative">
      {/* Navigation Buttons */}
      {totalPages > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 z-10 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-[#4A0404] hover:bg-[#4A0404] hover:text-white transition-all duration-300"
            aria-label="Previous testimonials"
            data-testid="testimonial-prev-btn"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 z-10 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-[#4A0404] hover:bg-[#4A0404] hover:text-white transition-all duration-300"
            aria-label="Next testimonials"
            data-testid="testimonial-next-btn"
          >
            <ChevronRight size={24} />
          </button>
        </>
      )}
      
      {/* Testimonials Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 transition-all duration-500">
        {visibleTestimonials.map((testimonial) => (
          <TestimonialCard key={testimonial.id} {...testimonial} />
        ))}
      </div>
      
      {/* Dots Indicator */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-8">
          {[...Array(totalPages)].map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentIndex 
                  ? 'bg-[#D4AF37] w-8' 
                  : 'bg-[#E5E0D8] hover:bg-[#D4AF37]/50'
              }`}
              aria-label={`Go to page ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Stats Component
const StatsSection = () => {
  const stats = [
    { number: "15+", label: "Years of Excellence" },
    { number: "5000+", label: "Happy Customers" },
    { number: "10000+", label: "Products Delivered" },
    { number: "100%", label: "Quality Assurance" },
  ];
  
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
      {stats.map((stat, index) => (
        <div key={index} className="text-center">
          <p className="font-playfair text-4xl md:text-5xl font-bold text-[#4A0404]">{stat.number}</p>
          <p className="font-manrope text-sm text-[#595959] mt-2">{stat.label}</p>
        </div>
      ))}
    </div>
  );
};

const Footer = () => {
  const { settings } = useSettings();
  
  // Parse address into lines for display
  const addressLines = settings.address ? settings.address.split('\n').filter(line => line.trim()) : [];
  const businessHoursLines = settings.business_hours ? settings.business_hours.split('\n').filter(line => line.trim()) : [];
  
  return (
    <footer className="bg-[#1A1A1A] text-white pt-20 pb-8">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Contact Us Section with Map */}
        <div className="mb-16 pb-16 border-b border-white/10">
          <div className="text-center mb-10">
            <p className="font-cormorant text-lg text-[#D4AF37] italic mb-2">Find Us</p>
            <h2 className="font-playfair text-3xl md:text-4xl font-bold text-white mb-4">Contact Us</h2>
            <div className="w-16 h-0.5 bg-[#D4AF37] mx-auto" />
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Map */}
            <div className="rounded-lg overflow-hidden card-shadow h-[300px] lg:h-[350px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3559.1234!2d80.9167!3d26.8456!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x399bfd07c2b0f5e1%3A0x6c5cdc36f7d8c7a5!2sAminabad%2C%20Lucknow%2C%20Uttar%20Pradesh!5e0!3m2!1sen!2sin!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="LT Cards & Creation Location"
              />
            </div>
            
            {/* Contact Details */}
            <div className="flex flex-col justify-center">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                    <MapPin size={20} className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <h4 className="font-playfair text-lg font-semibold text-white mb-2">Visit Our Store</h4>
                    <p className="font-manrope text-white/70 leading-relaxed whitespace-pre-line">
                      {settings.address || 'Shop No. 1 & 2, Mahaveer Market,\nNear Mehra Cinema, Aminabad,\nGwynne Road, Lucknow,\nUttar Pradesh - 226018'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                    <Phone size={20} className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <h4 className="font-playfair text-lg font-semibold text-white mb-2">Call Us</h4>
                    {(settings.phone_number || settings.whatsapp_number) ? (
                      <a href={`tel:${settings.phone_number || settings.whatsapp_number}`} className="font-manrope text-white/70 hover:text-[#D4AF37] transition-colors">
                        {settings.phone_number || settings.whatsapp_number}
                      </a>
                    ) : (
                      <p className="font-manrope text-white/70">Contact via WhatsApp</p>
                    )}
                    {settings.email && (
                      <p className="font-manrope text-white/70 mt-2">
                        <a href={`mailto:${settings.email}`} className="hover:text-[#D4AF37] transition-colors">
                          {settings.email}
                        </a>
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                    <Clock size={20} className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <h4 className="font-playfair text-lg font-semibold text-white mb-2">Business Hours</h4>
                    <p className="font-manrope text-white/70 whitespace-pre-line">
                      {settings.business_hours || 'Monday - Saturday: 10:00 AM - 8:00 PM\nSunday: Closed'}
                    </p>
                  </div>
                </div>
                
                {settings.whatsapp_number && (
                  <a 
                    href={`https://wa.me/${settings.whatsapp_number.replace(/[^0-9]/g, '')}?text=${encodeURIComponent('Hi! I would like to inquire about your products.')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 rounded-full font-manrope font-semibold hover:bg-[#20BA5C] transition-colors mt-4 w-fit"
                  >
                    <MessageCircle size={20} />
                    Chat on WhatsApp
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
          <div className="md:col-span-1">
            <h3 className="font-playfair text-3xl font-bold mb-4">
              <span className="text-[#D4AF37]">LT</span> Cards & Creation
            </h3>
            <p className="font-cormorant text-xl italic text-white/70 mb-4">
              Crafting Memories, Forever
            </p>
            <p className="font-manrope text-white/60 text-sm leading-relaxed">
              Premium printing solutions for all your special occasions. Quality craftsmanship that speaks elegance.
            </p>
          </div>
          
          <div>
            <h4 className="font-playfair text-lg font-semibold mb-6 text-[#D4AF37]">Collections</h4>
            <ul className="space-y-3">
              <li><Link to="/category/invitations" className="text-white/70 hover:text-[#D4AF37] text-sm transition-colors">Invitations</Link></li>
              <li><Link to="/category/paper-bags" className="text-white/70 hover:text-[#D4AF37] text-sm transition-colors">Paper Bags</Link></li>
              <li><Link to="/category/sagan-envelopes" className="text-white/70 hover:text-[#D4AF37] text-sm transition-colors">Sagan Envelopes</Link></li>
              <li><Link to="/category/boxes-hampers" className="text-white/70 hover:text-[#D4AF37] text-sm transition-colors">Boxes & Hampers</Link></li>
              <li><Link to="/category/grocery-pouches" className="text-white/70 hover:text-[#D4AF37] text-sm transition-colors">Grocery Pouches</Link></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-playfair text-lg font-semibold mb-6 text-[#D4AF37]">Company</h4>
            <ul className="space-y-3">
              <li><Link to="/about" className="text-white/70 hover:text-[#D4AF37] text-sm transition-colors">About Us</Link></li>
              <li><a href="#contact" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }); }} className="text-white/70 hover:text-[#D4AF37] text-sm transition-colors cursor-pointer">Contact Us</a></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-playfair text-lg font-semibold mb-6 text-[#D4AF37]">Get in Touch</h4>
            <div className="space-y-3">
              <p className="text-white/70 text-sm">
                <span className="text-white/40">Email:</span><br />
                {settings.email || 'info@ltcards.in'}
              </p>
              {settings.whatsapp_number && (
                <a 
                  href={`https://wa.me/${settings.whatsapp_number.replace(/[^0-9]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#25D366] text-white px-4 py-2 rounded-full text-sm hover:bg-[#20BA5C] transition-colors mt-4"
                >
                  <MessageCircle size={16} />
                  Chat on WhatsApp
                </a>
              )}
            </div>
          </div>
        </div>
        
        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-manrope text-white/40 text-sm">© 2024 LT Cards & Creation. All rights reserved.</p>
          <p className="font-cormorant text-white/40 text-sm italic">Made with love for your special moments</p>
        </div>
      </div>
    </footer>
  );
};

// Floating WhatsApp Button
const FloatingWhatsApp = () => {
  const { settings } = useSettings();
  
  if (!settings.whatsapp_number) return null;
  
  const whatsappUrl = `https://wa.me/${settings.whatsapp_number.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(settings.whatsapp_message || 'Hi! I\'m interested in your products.')}`;
  
  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 right-6 z-50 bg-[#25D366] text-white p-4 rounded-full shadow-lg hover:bg-[#20BA5C] transition-all duration-300 hover:scale-110 group"
      data-testid="floating-whatsapp-btn"
      aria-label="Chat on WhatsApp"
    >
      <MessageCircle size={28} className="group-hover:rotate-12 transition-transform" />
      <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-white text-[#1A1A1A] px-4 py-2 rounded-lg shadow-md font-manrope text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
        Chat with us
      </span>
    </a>
  );
};

// WhatsApp Button for Product Pages
const WhatsAppProductButton = ({ productName }) => {
  const { settings } = useSettings();
  
  if (!settings.whatsapp_number) return null;
  
  const message = `Hi! I'm interested in "${productName}". Can you please provide more details?`;
  const whatsappUrl = `https://wa.me/${settings.whatsapp_number.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
  
  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center justify-center bg-[#25D366] text-white px-6 py-3 rounded-md font-manrope font-semibold hover:bg-[#20BA5C] transition-all duration-300 hover:-translate-y-1 shadow-lg"
      data-testid="product-whatsapp-btn"
    >
      <MessageCircle size={20} className="mr-2" />
      Inquire on WhatsApp
    </a>
  );
};

// CTA Section with dynamic WhatsApp
const CTASection = () => {
  const { settings } = useSettings();
  
  const whatsappUrl = settings.whatsapp_number 
    ? `https://wa.me/${settings.whatsapp_number.replace(/[^0-9]/g, '')}?text=${encodeURIComponent('Hi! I would like to book a consultation.')}`
    : '#';
  
  return (
    <section className="cta-section py-20 relative">
      <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
        <p className="font-cormorant text-lg text-[#D4AF37] italic mb-2">Ready to Create?</p>
        <h2 className="font-playfair text-3xl md:text-5xl font-bold text-white mb-6">
          Book Your Style Consultation
        </h2>
        <p className="font-manrope text-white/80 mb-8 max-w-2xl mx-auto">
          Meet our team of experts who will understand your exact requirements and help bring your design dreams to life.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a 
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#D4AF37] text-[#1A1A1A] px-8 py-4 rounded-md font-manrope font-semibold hover:bg-[#c4a030] transition-colors inline-flex items-center justify-center"
            data-testid="cta-whatsapp-btn"
          >
            <MessageCircle size={20} className="mr-2" />
            Speak to an Expert
          </a>
        </div>
      </div>
    </section>
  );
};

// ============ PRODUCT REVIEWS SECTION ============
const ProductReviewsSection = ({ productId, productName }) => {
  const [reviews, setReviews] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '', customer_email: '', rating: 5, title: '', comment: ''
  });

  useEffect(() => {
    fetchReviews();
  }, [productId]);

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API}/reviews/product/${productId}`);
      setReviews(res.data);
    } catch (e) {
      console.error("Error fetching reviews:", e);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        product_id: productId,
        ...formData
      });
      toast.success('Thank you! Your review has been submitted for approval.');
      setShowForm(false);
      setFormData({ customer_name: '', customer_email: '', rating: 5, title: '', comment: '' });
    } catch (e) {
      toast.error('Failed to submit review. Please try again.');
    }
    setSubmitting(false);
  };

  const avgRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 0;

  return (
    <div className="mt-16 border-t border-[#E5E0D8] pt-16">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-playfair text-2xl font-bold text-[#1A1A1A]">Customer Reviews</h2>
          {reviews.length > 0 && (
            <div className="flex items-center gap-2 mt-2">
              <div className="flex text-[#D4AF37]">
                {[...Array(Math.round(avgRating))].map((_, i) => <Star key={i} size={18} fill="#D4AF37" />)}
              </div>
              <span className="font-manrope text-sm text-[#595959]">{avgRating} out of 5 ({reviews.length} reviews)</span>
            </div>
          )}
        </div>
        <button 
          onClick={() => setShowForm(!showForm)} 
          className="btn-outline"
          data-testid="write-review-btn"
        >
          {showForm ? 'Cancel' : 'Write a Review'}
        </button>
      </div>

      {/* Review Form */}
      {showForm && (
        <div className="bg-[#F9F7F2] rounded-lg p-6 mb-8 animate-fadeIn">
          <h3 className="font-playfair text-lg font-semibold mb-4">Share Your Experience</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block font-manrope text-sm font-medium mb-2">Your Name *</label>
                <input
                  type="text"
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope bg-white"
                  required
                  data-testid="review-name-input"
                />
              </div>
              <div>
                <label className="block font-manrope text-sm font-medium mb-2">Email (optional)</label>
                <input
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                  className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope bg-white"
                  data-testid="review-email-input"
                />
              </div>
            </div>
            <div>
              <label className="block font-manrope text-sm font-medium mb-2">Rating *</label>
              <div className="flex gap-2">
                {[1,2,3,4,5].map(num => (
                  <button
                    key={num}
                    type="button"
                    onClick={() => setFormData({ ...formData, rating: num })}
                    className="p-2 transition-transform hover:scale-110"
                  >
                    <Star 
                      size={28} 
                      className={formData.rating >= num ? 'text-[#D4AF37]' : 'text-[#E5E0D8]'}
                      fill={formData.rating >= num ? '#D4AF37' : 'none'} 
                    />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block font-manrope text-sm font-medium mb-2">Review Title (optional)</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope bg-white"
                placeholder="e.g., Excellent quality!"
                data-testid="review-title-input"
              />
            </div>
            <div>
              <label className="block font-manrope text-sm font-medium mb-2">Your Review *</label>
              <textarea
                value={formData.comment}
                onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope h-24 bg-white"
                placeholder="Share your experience with this product..."
                required
                data-testid="review-comment-input"
              />
            </div>
            <button 
              type="submit" 
              disabled={submitting}
              className="btn-primary disabled:opacity-50"
              data-testid="submit-review-btn"
            >
              {submitting ? 'Submitting...' : 'Submit Review'}
            </button>
          </form>
        </div>
      )}

      {/* Reviews List */}
      {reviews.length > 0 ? (
        <div className="space-y-6">
          {reviews.map((review) => (
            <div key={review.id} className="bg-white p-6 rounded-lg card-shadow">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex text-[#D4AF37] mb-1">
                    {[...Array(review.rating)].map((_, i) => <Star key={i} size={16} fill="#D4AF37" />)}
                  </div>
                  {review.title && <h4 className="font-manrope font-semibold text-[#1A1A1A]">{review.title}</h4>}
                </div>
                <span className="font-manrope text-xs text-[#595959]">
                  {new Date(review.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              </div>
              <p className="font-manrope text-[#595959] mb-3">{review.comment}</p>
              <p className="font-manrope text-sm text-[#4A0404]">— {review.customer_name}</p>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white p-8 rounded-lg card-shadow text-center">
          <Star size={40} className="mx-auto text-[#E5E0D8] mb-3" />
          <p className="font-manrope text-[#595959]">No reviews yet. Be the first to review this product!</p>
        </div>
      )}
    </div>
  );
};

// ============ STOREFRONT PAGES ============

const HomePage = ({ categories }) => {
  const [testimonials, setTestimonials] = useState([]);
  const [loadingTestimonials, setLoadingTestimonials] = useState(true);
  const [blogPosts, setBlogPosts] = useState([]);
  const [loadingBlog, setLoadingBlog] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const res = await axios.get(`${API}/testimonials`);
        setTestimonials(res.data);
      } catch (e) {
        console.error("Error fetching testimonials:", e);
        setTestimonials([
          { id: '1', name: "Priya Sharma", text: "The quality of invitations exceeded our expectations. Truly premium craftsmanship!", rating: 5 },
          { id: '2', name: "Rajesh Kumar", text: "Excellent service and beautiful paper bags. Will definitely order again.", rating: 5 },
          { id: '3', name: "Anitha Rajan", text: "LT Cards & Creation made our wedding invitations absolutely stunning. Highly recommended!", rating: 5 },
        ]);
      }
      setLoadingTestimonials(false);
    };
    
    const fetchBlogPosts = async () => {
      try {
        const res = await axios.get(`${API}/blog`);
        setBlogPosts(res.data.slice(0, 3)); // Show only 3 latest posts
      } catch (e) {
        console.error("Error fetching blog posts:", e);
      }
      setLoadingBlog(false);
    };
    
    fetchTestimonials();
    fetchBlogPosts();
  }, []);

  return (
    <div>
      <SEO 
        title="Premium Wedding Invitations, Paper Bags & Packaging Solutions in Lucknow"
        description="LT Cards & Creation offers premium quality wedding invitations, paper bags, sagan envelopes, boxes, hampers & grocery pouches in Lucknow. Crafting memories with elegant designs."
      />
      <HeroSection />
      
      {/* Category Showcase - Horizontal Scroll */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="font-cormorant text-lg text-[#D4AF37] italic mb-2">Explore</p>
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-[#1A1A1A] mb-4">Our Collections</h2>
            <div className="gold-line" />
            <p className="font-manrope text-[#595959] max-w-2xl mx-auto mt-4">
              Discover our range of premium products, crafted with precision and elegance for your special moments.
            </p>
          </div>
        </div>
        
        {/* Horizontal Scrolling Categories */}
        <div className="horizontal-scroll px-6 md:px-12">
          <div className="w-4 flex-shrink-0" /> {/* Spacer */}
          {categories.filter(c => !c.is_page).map((cat, index) => (
            <CategoryCard key={cat.id} category={cat} index={index} />
          ))}
          <div className="w-4 flex-shrink-0" /> {/* Spacer */}
        </div>
      </section>
      
      {/* Stats Section */}
      <section className="py-20 bg-[#F9F7F2]">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <StatsSection />
        </div>
      </section>
      
      {/* About Preview Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="animate-fadeInLeft opacity-0" style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}>
              <p className="font-cormorant text-lg text-[#D4AF37] italic mb-2">Our Story</p>
              <h2 className="font-playfair text-4xl md:text-5xl font-bold text-[#1A1A1A] mb-6">
                Crafting Excellence<br />Since Day One
              </h2>
              <div className="gold-border-left">
                <p className="font-manrope text-[#595959] leading-relaxed mb-6">
                  At LT Cards & Creation, we believe that every special occasion deserves a touch of elegance. Our commitment to quality craftsmanship and attention to detail has made us a trusted name in premium printing solutions.
                </p>
                <p className="font-manrope text-[#595959] leading-relaxed">
                  From luxurious wedding invitations to premium packaging, we bring your vision to life with unmatched precision and care.
                </p>
              </div>
              <Link to="/about" className="btn-primary inline-flex items-center mt-8">
                Learn More About Us
                <ChevronRight size={18} className="ml-2" />
              </Link>
            </div>
            <div className="relative animate-fadeInRight opacity-0" style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}>
              <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.pexels.com/photos/6620989/pexels-photo-6620989.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260"
                  alt="About LT Cards & Creation"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Decorative element */}
              <div className="absolute -bottom-6 -left-6 w-32 h-32 border-2 border-[#D4AF37] rounded-2xl -z-10" />
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <CTASection />
      
      {/* Testimonials */}
      <section className="py-24 bg-[#F9F7F2]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="font-cormorant text-lg text-[#D4AF37] italic mb-2">Testimonials</p>
            <h2 className="font-playfair text-4xl font-bold text-[#1A1A1A] mb-4">What Our Customers Say</h2>
            <div className="gold-line" />
          </div>
          
          {loadingTestimonials ? (
            <div className="text-center py-8">
              <div className="animate-pulse font-manrope text-[#595959]">Loading testimonials...</div>
            </div>
          ) : testimonials.length > 0 ? (
            <TestimonialsSlider testimonials={testimonials} />
          ) : (
            <div className="text-center py-8">
              <p className="font-manrope text-[#595959]">No testimonials yet.</p>
            </div>
          )}
        </div>
      </section>
      
      {/* Blog Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="font-cormorant text-lg text-[#D4AF37] italic mb-2">Our Blog</p>
            <h2 className="font-playfair text-4xl font-bold text-[#1A1A1A] mb-4">Tips & Inspiration</h2>
            <div className="gold-line" />
          </div>
          
          {loadingBlog ? (
            <div className="text-center py-8">
              <div className="animate-pulse font-manrope text-[#595959]">Loading...</div>
            </div>
          ) : blogPosts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {blogPosts.map((post) => (
                <Link to={`/blog/${post.slug}`} key={post.id} className="group">
                  <div className="bg-[#F9F7F2] rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                    {post.image_url && (
                      <div className="aspect-video overflow-hidden">
                        <img 
                          src={post.image_url} 
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-center gap-2 text-sm text-[#595959] mb-3">
                        <Calendar size={14} />
                        {new Date(post.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </div>
                      <h3 className="font-playfair text-xl font-semibold text-[#1A1A1A] mb-2 group-hover:text-[#4A0404] transition-colors">
                        {post.title}
                      </h3>
                      <p className="font-manrope text-[#595959] text-sm line-clamp-2">
                        {post.excerpt}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-[#F9F7F2] rounded-lg">
              <BookOpen size={40} className="mx-auto text-[#D4AF37] mb-4" />
              <h3 className="font-playfair text-xl text-[#1A1A1A] mb-2">Coming Soon</h3>
              <p className="font-manrope text-[#595959]">Exciting content is on the way!</p>
            </div>
          )}
          
          <div className="text-center mt-10">
            <Link to="/blog" className="btn-outline inline-flex items-center gap-2">
              <BookOpen size={18} />
              View All Posts
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

const CategoryPage = ({ categories }) => {
  const { slug } = useParams();
  const api = useApi();
  const [category, setCategory] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const catRes = await api.get(`/categories/slug/${slug}`);
        setCategory(catRes.data);
        
        const subRes = await api.get(`/subcategories/category/${catRes.data.id}`);
        setSubcategories(subRes.data);
        
        const prodRes = await api.get(`/products/category/${catRes.data.id}`);
        setProducts(prodRes.data);
      } catch (e) {
        console.error("Error fetching category:", e);
        toast.error("Failed to load category");
      }
      setLoading(false);
    };
    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse font-manrope text-[#595959]">Loading...</div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-playfair text-2xl mb-4">Category not found</h2>
          <Link to="/" className="btn-primary">Go Home</Link>
        </div>
      </div>
    );
  }

  // SEO descriptions for each category
  const categoryDescriptions = {
    'invitations': 'Browse our premium collection of wedding invitations, engagement cards & designer invitation cards in Lucknow. Traditional & modern designs.',
    'paper-bags': 'Quality paper bags for gifts, shopping & packaging. Custom printed paper bags available. Best paper bag supplier in Lucknow.',
    'sagan-envelopes': 'Beautiful sagan envelopes & shagun covers for weddings and ceremonies. Premium quality with elegant designs in Lucknow.',
    'boxes-hampers': 'Gift boxes, hampers & premium packaging solutions. Perfect for weddings, festivals & corporate gifting in Lucknow.',
    'grocery-pouches': 'Durable grocery pouches & packaging bags. Eco-friendly options available. Wholesale supplier in Lucknow.'
  };

  return (
    <div>
      <SEO 
        title={`${category?.name || 'Category'} - Premium ${category?.name || 'Products'} in Lucknow`}
        description={categoryDescriptions[slug] || `Shop premium ${category?.name || 'products'} at LT Cards & Creation, Lucknow. Quality craftsmanship and elegant designs.`}
      />
      <CategoryTabs categories={categories} activeSlug={slug} />
      
      {/* Hero Banner */}
      <div className="relative h-64 md:h-80">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${category.image_url || 'https://images.pexels.com/photos/7405670/pexels-photo-7405670.jpeg'})` }}
        >
          <div className="absolute inset-0 bg-[#4A0404]/60"></div>
        </div>
        <div className="relative z-10 h-full flex items-center justify-center">
          <h1 className="font-playfair text-4xl md:text-5xl text-white font-bold animate-fadeIn">
            {category.name}
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        {/* Subcategories */}
        {subcategories.length > 0 && (
          <div className="mb-16">
            <h2 className="font-playfair text-2xl font-semibold mb-8">Browse by Type</h2>
            <div className="flex flex-wrap gap-3">
              {subcategories.map((sub) => (
                <Link
                  key={sub.id}
                  to={`/subcategory/${sub.slug}`}
                  className="px-6 py-2 border border-[#E5E0D8] rounded-full font-manrope text-sm hover:border-[#4A0404] hover:text-[#4A0404] transition-colors"
                  data-testid={`subcategory-link-${sub.slug}`}
                >
                  {sub.name}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Products */}
        {products.length > 0 ? (
          <div>
            <h2 className="font-playfair text-2xl font-semibold mb-8">All Products</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-16">
            <ShoppingBag size={48} className="mx-auto text-[#E5E0D8] mb-4" />
            <p className="font-manrope text-[#595959]">No products available yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

const SubcategoryPage = ({ categories }) => {
  const { slug } = useParams();
  const api = useApi();
  const [subcategory, setSubcategory] = useState(null);
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const subRes = await api.get(`/subcategories/slug/${slug}`);
        setSubcategory(subRes.data);
        
        const catRes = await api.get(`/categories/${subRes.data.category_id}`);
        setCategory(catRes.data);
        
        const prodRes = await api.get(`/products/subcategory/${subRes.data.id}`);
        setProducts(prodRes.data);
      } catch (e) {
        console.error("Error fetching subcategory:", e);
        toast.error("Failed to load subcategory");
      }
      setLoading(false);
    };
    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse font-manrope text-[#595959]">Loading...</div>
      </div>
    );
  }

  if (!subcategory) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-playfair text-2xl mb-4">Subcategory not found</h2>
          <Link to="/" className="btn-primary">Go Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      {category && <CategoryTabs categories={categories} activeSlug={category.slug} />}
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <nav className="flex items-center space-x-2 text-sm font-manrope mb-8">
          <Link to="/" className="text-[#595959] hover:text-[#4A0404]">Home</Link>
          <ChevronRight size={14} className="text-[#595959]" />
          {category && (
            <>
              <Link to={`/category/${category.slug}`} className="text-[#595959] hover:text-[#4A0404]">{category.name}</Link>
              <ChevronRight size={14} className="text-[#595959]" />
            </>
          )}
          <span className="text-[#4A0404]">{subcategory.name}</span>
        </nav>

        <h1 className="font-playfair text-4xl font-bold text-[#1A1A1A] mb-12">{subcategory.name}</h1>

        {products.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <ShoppingBag size={48} className="mx-auto text-[#E5E0D8] mb-4" />
            <p className="font-manrope text-[#595959]">No products available in this category</p>
          </div>
        )}
      </div>
    </div>
  );
};

const ProductPage = () => {
  const { slug } = useParams();
  const api = useApi();
  const [product, setProduct] = useState(null);
  const [category, setCategory] = useState(null);
  const [subcategory, setSubcategory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const prodRes = await api.get(`/products/slug/${slug}`);
        setProduct(prodRes.data);
        
        const catRes = await api.get(`/categories/${prodRes.data.category_id}`);
        setCategory(catRes.data);
        
        const subRes = await api.get(`/subcategories/${prodRes.data.subcategory_id}`);
        setSubcategory(subRes.data);
      } catch (e) {
        console.error("Error fetching product:", e);
        toast.error("Failed to load product");
      }
      setLoading(false);
    };
    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse font-manrope text-[#595959]">Loading...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-playfair text-2xl mb-4">Product not found</h2>
          <Link to="/" className="btn-primary">Go Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
      <SEO 
        title={`${product?.name || 'Product'} - Buy Online in Lucknow`}
        description={product?.description || `Buy ${product?.name} at LT Cards & Creation, Lucknow. Premium quality products.`}
      />
      <nav className="flex items-center space-x-2 text-sm font-manrope mb-8">
        <Link to="/" className="text-[#595959] hover:text-[#4A0404]">Home</Link>
        <ChevronRight size={14} className="text-[#595959]" />
        {category && (
          <>
            <Link to={`/category/${category.slug}`} className="text-[#595959] hover:text-[#4A0404]">{category.name}</Link>
            <ChevronRight size={14} className="text-[#595959]" />
          </>
        )}
        {subcategory && (
          <>
            <Link to={`/subcategory/${subcategory.slug}`} className="text-[#595959] hover:text-[#4A0404]">{subcategory.name}</Link>
            <ChevronRight size={14} className="text-[#595959]" />
          </>
        )}
        <span className="text-[#4A0404]">{product.name}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
        {/* Product Gallery */}
        <ProductGallery 
          images={[
            product.image_url,
            ...(product.images || [])
          ].filter(Boolean)}
          productName={product.name}
        />
        
        {/* Product Details */}
        <div className="lg:py-4">
          <span className="inline-block px-3 py-1 bg-[#F9F7F2] text-[#D4AF37] text-xs font-manrope font-semibold uppercase tracking-wider rounded-full mb-4">
            {subcategory?.name || 'Premium'}
          </span>
          <h1 className="font-playfair text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-4">{product.name}</h1>
          <p className="font-playfair text-3xl text-[#4A0404] font-bold mb-6">₹{product.price.toLocaleString()}</p>
          
          <div className="gold-line mb-6" style={{ margin: '0 0 1.5rem 0' }} />
          
          {product.description && (
            <div className="mb-8">
              <h3 className="font-manrope font-semibold mb-3 text-[#1A1A1A]">Description</h3>
              <p className="font-manrope text-[#595959] leading-relaxed">{product.description}</p>
            </div>
          )}
          
          {/* Features/Benefits */}
          <div className="bg-[#F9F7F2] rounded-lg p-6 mb-8">
            <h3 className="font-manrope font-semibold mb-4 text-[#1A1A1A]">Why Choose This?</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="w-5 h-5 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="w-2 h-2 rounded-full bg-[#D4AF37]" />
                </span>
                <span className="font-manrope text-sm text-[#595959]">Premium quality materials</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-5 h-5 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="w-2 h-2 rounded-full bg-[#D4AF37]" />
                </span>
                <span className="font-manrope text-sm text-[#595959]">Customizable designs available</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-5 h-5 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="w-2 h-2 rounded-full bg-[#D4AF37]" />
                </span>
                <span className="font-manrope text-sm text-[#595959]">Fast turnaround time</span>
              </li>
            </ul>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <WhatsAppProductButton productName={product.name} />
            <button className="btn-outline" data-testid="contact-btn">
              <Phone size={18} className="inline mr-2" />
              Call for Inquiry
            </button>
          </div>
        </div>
      </div>

      {/* Product Reviews Section */}
      <ProductReviewsSection productId={product.id} productName={product.name} />
    </div>
  );
};

const AboutPage = () => {
  const api = useApi();
  const [about, setAbout] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAbout = async () => {
      try {
        const res = await api.get('/about');
        setAbout(res.data);
      } catch (e) {
        console.error("Error fetching about:", e);
      }
      setLoading(false);
    };
    fetchAbout();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse font-manrope text-[#595959]">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
      <SEO 
        title="About Us - Our Story & Journey"
        description="Learn about LT Cards & Creation - Lucknow's trusted name for premium wedding invitations, paper bags & packaging."
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div className="animate-fadeIn">
          <h1 className="font-playfair text-5xl font-bold text-[#1A1A1A] mb-6">
            {about?.title || 'About LT Cards & Creation'}
          </h1>
          <p className="font-manrope text-[#595959] text-lg leading-relaxed whitespace-pre-line">
            {about?.content || 'Welcome to LT Cards & Creation - Your premium destination for quality printing products including invitations, paper bags, envelopes, boxes, and more.'}
          </p>
        </div>
        <div className="aspect-square rounded-lg overflow-hidden card-shadow animate-fadeIn stagger-2" style={{ opacity: 0 }}>
          <img 
            src={about?.image_url || 'https://images.pexels.com/photos/6620989/pexels-photo-6620989.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'} 
            alt="About Us"
            className="w-full h-full object-cover"
          />
        </div>
      </div>
    </div>
  );
};

// ============ ADMIN COMPONENTS ============

const AdminLogin = () => {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/admin/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      login(res.data.token, res.data.username);
      toast.success('Login successful');
      navigate('/admin/dashboard');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Login failed');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#F9F7F2]">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="font-playfair text-3xl font-bold text-[#1A1A1A]">Admin Login</h1>
          <p className="font-manrope text-[#595959] mt-2">Sign in to manage your store</p>
        </div>
        
        <form onSubmit={handleSubmit} className="bg-white p-8 rounded-lg card-shadow">
          <div className="mb-6">
            <label className="block font-manrope text-sm font-medium text-[#1A1A1A] mb-2">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope focus:outline-none focus:border-[#4A0404]"
              required
              data-testid="login-username"
            />
          </div>
          <div className="mb-6">
            <label className="block font-manrope text-sm font-medium text-[#1A1A1A] mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope focus:outline-none focus:border-[#4A0404]"
              required
              data-testid="login-password"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full btn-primary disabled:opacity-50"
            data-testid="login-submit"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        
        <p className="text-center mt-6 font-manrope text-sm text-[#595959]">
          <Link to="/" className="text-[#4A0404] hover:underline">← Back to Store</Link>
        </p>
      </div>
    </div>
  );
};

const AdminSidebar = ({ activeTab }) => {
  const { logout, username } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/admin');
  };

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Grid3X3, path: '/admin/dashboard' },
    { id: 'categories', label: 'Categories', icon: FileText, path: '/admin/categories' },
    { id: 'subcategories', label: 'Subcategories', icon: Package, path: '/admin/subcategories' },
    { id: 'products', label: 'Products', icon: ShoppingBag, path: '/admin/products' },
    { id: 'testimonials', label: 'Testimonials', icon: Quote, path: '/admin/testimonials' },
    { id: 'reviews', label: 'Product Reviews', icon: Star, path: '/admin/reviews' },
    { id: 'blog', label: 'Blog Posts', icon: BookOpen, path: '/admin/blog' },
    { id: 'about', label: 'About Page', icon: FileText, path: '/admin/about' },
    { id: 'settings', label: 'Settings', icon: Settings, path: '/admin/settings' },
  ];

  return (
    <aside className="w-64 bg-white border-r border-[#E5E0D8] min-h-screen flex flex-col">
      <div className="p-6 border-b border-[#E5E0D8]">
        <Link to="/" className="font-playfair text-xl font-bold text-[#4A0404]">LT Cards & Creation</Link>
        <p className="font-manrope text-xs text-[#595959] mt-1">Admin Panel</p>
      </div>
      
      <nav className="flex-1 p-4">
        {menuItems.map((item) => (
          <Link
            key={item.id}
            to={item.path}
            className={`flex items-center space-x-3 px-4 py-3 rounded-md mb-1 font-manrope text-sm transition-colors ${
              activeTab === item.id 
                ? 'bg-[#4A0404] text-white' 
                : 'text-[#595959] hover:bg-[#F9F7F2]'
            }`}
            data-testid={`admin-nav-${item.id}`}
          >
            <item.icon size={18} />
            <span>{item.label}</span>
          </Link>
        ))}
      </nav>
      
      <div className="p-4 border-t border-[#E5E0D8]">
        <div className="flex items-center justify-between mb-4">
          <span className="font-manrope text-sm text-[#595959]">{username}</span>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center space-x-2 text-[#595959] hover:text-[#4A0404] font-manrope text-sm"
          data-testid="admin-logout"
        >
          <LogOut size={16} />
          <span>Logout</span>
        </button>
        <Link
          to="/"
          className="flex items-center space-x-2 text-[#595959] hover:text-[#4A0404] font-manrope text-sm mt-3"
          data-testid="admin-view-store"
        >
          <Eye size={16} />
          <span>View Store</span>
        </Link>
      </div>
    </aside>
  );
};

const AdminLayout = ({ children, activeTab }) => {
  const { isAuthenticated } = useAuth();
  
  if (!isAuthenticated) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <div className="flex min-h-screen bg-[#F9F7F2]">
      <AdminSidebar activeTab={activeTab} />
      <main className="flex-1 p-8">
        {children}
      </main>
    </div>
  );
};

const AdminDashboard = () => {
  const api = useApi();
  const [stats, setStats] = useState({ categories: 0, subcategories: 0, products: 0 });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [cats, subs, prods] = await Promise.all([
          api.get('/categories'),
          api.get('/subcategories'),
          api.get('/products'),
        ]);
        setStats({
          categories: cats.data.length,
          subcategories: subs.data.length,
          products: prods.data.length,
        });
      } catch (e) {
        console.error("Error fetching stats:", e);
      }
    };
    fetchStats();
  }, []);

  return (
    <AdminLayout activeTab="dashboard">
      <h1 className="font-playfair text-3xl font-bold text-[#1A1A1A] mb-8">Dashboard</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg card-shadow">
          <h3 className="font-manrope text-sm text-[#595959] mb-2">Categories</h3>
          <p className="font-playfair text-4xl font-bold text-[#4A0404]">{stats.categories}</p>
        </div>
        <div className="bg-white p-6 rounded-lg card-shadow">
          <h3 className="font-manrope text-sm text-[#595959] mb-2">Subcategories</h3>
          <p className="font-playfair text-4xl font-bold text-[#4A0404]">{stats.subcategories}</p>
        </div>
        <div className="bg-white p-6 rounded-lg card-shadow">
          <h3 className="font-manrope text-sm text-[#595959] mb-2">Products</h3>
          <p className="font-playfair text-4xl font-bold text-[#4A0404]">{stats.products}</p>
        </div>
      </div>
      
      <div className="bg-white p-6 rounded-lg card-shadow">
        <h2 className="font-playfair text-xl font-semibold mb-4">Quick Actions</h2>
        <div className="flex flex-wrap gap-4">
          <Link to="/admin/subcategories" className="btn-outline inline-flex items-center" data-testid="quick-add-subcategory">
            <Plus size={16} className="mr-2" /> Add Subcategory
          </Link>
          <Link to="/admin/products" className="btn-primary inline-flex items-center" data-testid="quick-add-product">
            <Plus size={16} className="mr-2" /> Add Product
          </Link>
        </div>
      </div>
    </AdminLayout>
  );
};

const AdminCategories = () => {
  const api = useApi();
  const { token } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', slug: '', image_url: '', order: 0, is_page: false });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await api.get('/categories');
      setCategories(res.data);
    } catch (e) {
      toast.error("Failed to load categories");
    }
    setLoading(false);
  };

  const seedCategories = async () => {
    try {
      await api.post('/admin/seed', {});
      toast.success('Categories seeded successfully');
      fetchCategories();
    } catch (e) {
      toast.error("Failed to seed categories");
    }
  };

  const handleEdit = (cat) => {
    setFormData({
      name: cat.name,
      slug: cat.slug,
      image_url: cat.image_url || '',
      order: cat.order,
      is_page: cat.is_page
    });
    setEditingId(cat.id);
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData({ name: '', slug: '', image_url: '', order: 0, is_page: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/categories/${editingId}`, formData);
      toast.success('Category updated successfully');
      setEditingId(null);
      setFormData({ name: '', slug: '', image_url: '', order: 0, is_page: false });
      fetchCategories();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update category');
    }
  };

  return (
    <AdminLayout activeTab="categories">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-playfair text-3xl font-bold text-[#1A1A1A]">Categories</h1>
        <button onClick={seedCategories} className="btn-outline" data-testid="seed-categories-btn">
          Seed Default Categories
        </button>
      </div>

      {/* Edit Form */}
      {editingId && (
        <div className="bg-white p-6 rounded-lg card-shadow mb-8">
          <h2 className="font-playfair text-xl font-semibold mb-4">Edit Category</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block font-manrope text-sm font-medium mb-2">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope"
                  required
                  data-testid="category-name-input"
                />
              </div>
              <div>
                <label className="block font-manrope text-sm font-medium mb-2">Slug</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope"
                  required
                  data-testid="category-slug-input"
                />
              </div>
            </div>
            <div>
              <label className="block font-manrope text-sm font-medium mb-2">Image URL</label>
              <input
                type="url"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope"
                placeholder="https://example.com/image.jpg"
                data-testid="category-image-input"
              />
              {formData.image_url && (
                <div className="mt-3">
                  <p className="font-manrope text-sm text-[#595959] mb-2">Preview:</p>
                  <img 
                    src={formData.image_url} 
                    alt="Category preview" 
                    className="w-32 h-32 object-cover rounded-lg border border-[#E5E0D8]"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block font-manrope text-sm font-medium mb-2">Display Order</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope"
                  data-testid="category-order-input"
                />
              </div>
              <div className="flex items-center pt-8">
                <input
                  type="checkbox"
                  id="is_page"
                  checked={formData.is_page}
                  onChange={(e) => setFormData({ ...formData, is_page: e.target.checked })}
                  className="w-4 h-4 mr-2"
                />
                <label htmlFor="is_page" className="font-manrope text-sm">Is Page (e.g., About Us)</label>
              </div>
            </div>
            <div className="flex gap-4">
              <button type="submit" className="btn-primary" data-testid="category-save-btn">
                Save Changes
              </button>
              <button type="button" onClick={handleCancel} className="btn-outline" data-testid="category-cancel-btn">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
      
      <div className="bg-white rounded-lg card-shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#F9F7F2]">
            <tr>
              <th className="px-6 py-4 text-left font-manrope text-sm font-semibold text-[#595959]">Image</th>
              <th className="px-6 py-4 text-left font-manrope text-sm font-semibold text-[#595959]">Name</th>
              <th className="px-6 py-4 text-left font-manrope text-sm font-semibold text-[#595959]">Slug</th>
              <th className="px-6 py-4 text-left font-manrope text-sm font-semibold text-[#595959]">Type</th>
              <th className="px-6 py-4 text-left font-manrope text-sm font-semibold text-[#595959]">Order</th>
              <th className="px-6 py-4 text-left font-manrope text-sm font-semibold text-[#595959]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((cat) => (
              <tr key={cat.id} className="border-t border-[#E5E0D8]">
                <td className="px-6 py-4">
                  {cat.image_url ? (
                    <img 
                      src={cat.image_url} 
                      alt={cat.name} 
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-[#F9F7F2] rounded-lg flex items-center justify-center">
                      <ImageIcon size={24} className="text-[#E5E0D8]" />
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 font-manrope font-medium">{cat.name}</td>
                <td className="px-6 py-4 font-manrope text-[#595959]">{cat.slug}</td>
                <td className="px-6 py-4 font-manrope text-[#595959]">{cat.is_page ? 'Page' : 'Category'}</td>
                <td className="px-6 py-4 font-manrope text-[#595959]">{cat.order}</td>
                <td className="px-6 py-4">
                  <button 
                    onClick={() => handleEdit(cat)} 
                    className="text-[#4A0404] hover:underline inline-flex items-center gap-1"
                    data-testid={`edit-category-${cat.slug}`}
                  >
                    <Edit2 size={16} /> Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {categories.length === 0 && !loading && (
          <div className="text-center py-12">
            <p className="font-manrope text-[#595959]">No categories yet. Click "Seed Default Categories" to get started.</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

const AdminSubcategories = () => {
  const api = useApi();
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', slug: '', category_id: '', image_url: '', order: 0 });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [catsRes, subsRes] = await Promise.all([
        api.get('/categories'),
        api.get('/subcategories'),
      ]);
      setCategories(catsRes.data.filter(c => !c.is_page));
      setSubcategories(subsRes.data);
    } catch (e) {
      toast.error("Failed to load data");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/admin/subcategories/${editingId}`, formData);
        toast.success('Subcategory updated');
      } else {
        await api.post('/admin/subcategories', formData);
        toast.success('Subcategory created');
      }
      setShowForm(false);
      setEditingId(null);
      setFormData({ name: '', slug: '', category_id: '', image_url: '', order: 0 });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (sub) => {
    setFormData({
      name: sub.name,
      slug: sub.slug,
      category_id: sub.category_id,
      image_url: sub.image_url || '',
      order: sub.order,
    });
    setEditingId(sub.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure? This will also delete all products in this subcategory.')) return;
    try {
      await api.delete(`/admin/subcategories/${id}`);
      toast.success('Subcategory deleted');
      fetchData();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const getCategoryName = (id) => categories.find(c => c.id === id)?.name || 'Unknown';

  return (
    <AdminLayout activeTab="subcategories">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-playfair text-3xl font-bold text-[#1A1A1A]">Subcategories</h1>
        <button 
          onClick={() => { setShowForm(true); setEditingId(null); setFormData({ name: '', slug: '', category_id: '', image_url: '', order: 0 }); }} 
          className="btn-primary inline-flex items-center"
          data-testid="add-subcategory-btn"
        >
          <Plus size={16} className="mr-2" /> Add Subcategory
        </button>
      </div>

      {showForm && (
        <div className="bg-white p-6 rounded-lg card-shadow mb-8">
          <h2 className="font-playfair text-xl font-semibold mb-4">{editingId ? 'Edit' : 'Add'} Subcategory</h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block font-manrope text-sm font-medium mb-2">Category</label>
              <select
                value={formData.category_id}
                onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope"
                required
                data-testid="subcategory-category-select"
              >
                <option value="">Select Category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block font-manrope text-sm font-medium mb-2">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') })}
                className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope"
                required
                data-testid="subcategory-name-input"
              />
            </div>
            <div>
              <label className="block font-manrope text-sm font-medium mb-2">Slug</label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope"
                required
                data-testid="subcategory-slug-input"
              />
            </div>
            <div>
              <label className="block font-manrope text-sm font-medium mb-2">Image URL</label>
              <input
                type="url"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope"
                data-testid="subcategory-image-input"
              />
            </div>
            <div>
              <label className="block font-manrope text-sm font-medium mb-2">Order</label>
              <input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope"
                data-testid="subcategory-order-input"
              />
            </div>
            <div className="md:col-span-2 flex space-x-4">
              <button type="submit" className="btn-primary" data-testid="subcategory-submit-btn">
                {editingId ? 'Update' : 'Create'}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="btn-outline">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
      
      <div className="bg-white rounded-lg card-shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#F9F7F2]">
            <tr>
              <th className="px-6 py-4 text-left font-manrope text-sm font-semibold text-[#595959]">Name</th>
              <th className="px-6 py-4 text-left font-manrope text-sm font-semibold text-[#595959]">Category</th>
              <th className="px-6 py-4 text-left font-manrope text-sm font-semibold text-[#595959]">Slug</th>
              <th className="px-6 py-4 text-left font-manrope text-sm font-semibold text-[#595959]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {subcategories.map((sub) => (
              <tr key={sub.id} className="border-t border-[#E5E0D8]">
                <td className="px-6 py-4 font-manrope">{sub.name}</td>
                <td className="px-6 py-4 font-manrope text-[#595959]">{getCategoryName(sub.category_id)}</td>
                <td className="px-6 py-4 font-manrope text-[#595959]">{sub.slug}</td>
                <td className="px-6 py-4">
                  <button onClick={() => handleEdit(sub)} className="text-[#4A0404] hover:underline mr-4" data-testid={`edit-subcategory-${sub.id}`}>
                    <Edit2 size={16} />
                  </button>
                  <button onClick={() => handleDelete(sub.id)} className="text-[#9B2226] hover:underline" data-testid={`delete-subcategory-${sub.id}`}>
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {subcategories.length === 0 && (
          <div className="text-center py-12">
            <p className="font-manrope text-[#595959]">No subcategories yet. Click "Add Subcategory" to get started.</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

const AdminProducts = () => {
  const api = useApi();
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '', slug: '', category_id: '', subcategory_id: '', description: '', price: 0, image_url: '', images: [], is_featured: false, is_active: true
  });
  const [newImageUrl, setNewImageUrl] = useState('');
  const [draggedIndex, setDraggedIndex] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [catsRes, subsRes, prodsRes] = await Promise.all([
        api.get('/categories'),
        api.get('/subcategories'),
        api.get('/products'),
      ]);
      setCategories(catsRes.data.filter(c => !c.is_page));
      setSubcategories(subsRes.data);
      setProducts(prodsRes.data);
    } catch (e) {
      toast.error("Failed to load data");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData, price: parseFloat(formData.price) };
      if (editingId) {
        await api.put(`/admin/products/${editingId}`, submitData);
        toast.success('Product updated');
      } else {
        await api.post('/admin/products', submitData);
        toast.success('Product created');
      }
      setShowForm(false);
      setEditingId(null);
      setFormData({ name: '', slug: '', category_id: '', subcategory_id: '', description: '', price: 0, image_url: '', images: [], is_featured: false, is_active: true });
      setNewImageUrl('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (prod) => {
    setFormData({
      name: prod.name,
      slug: prod.slug,
      category_id: prod.category_id,
      subcategory_id: prod.subcategory_id,
      description: prod.description || '',
      price: prod.price,
      image_url: prod.image_url || '',
      images: prod.images || [],
      is_featured: prod.is_featured,
      is_active: prod.is_active,
    });
    setEditingId(prod.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success('Product deleted');
      fetchData();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  // Image management functions
  const addImage = () => {
    if (newImageUrl && newImageUrl.trim()) {
      setFormData({ ...formData, images: [...formData.images, newImageUrl.trim()] });
      setNewImageUrl('');
    }
  };

  const removeImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData({ ...formData, images: newImages });
  };

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    const newImages = [...formData.images];
    const draggedItem = newImages[draggedIndex];
    newImages.splice(draggedIndex, 1);
    newImages.splice(index, 0, draggedItem);
    
    setFormData({ ...formData, images: newImages });
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const setAsMainImage = (index) => {
    const imageToMove = formData.images[index];
    const newImages = formData.images.filter((_, i) => i !== index);
    // Swap main image with gallery image
    if (formData.image_url) {
      newImages.unshift(formData.image_url);
    }
    setFormData({ ...formData, image_url: imageToMove, images: newImages });
    toast.success('Set as main image');
  };

  const filteredSubcategories = subcategories.filter(s => s.category_id === formData.category_id);

  return (
    <AdminLayout activeTab="products">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-playfair text-3xl font-bold text-[#1A1A1A]">Products</h1>
        <button 
          onClick={() => { setShowForm(true); setEditingId(null); setFormData({ name: '', slug: '', category_id: '', subcategory_id: '', description: '', price: 0, image_url: '', images: [], is_featured: false, is_active: true }); }} 
          className="btn-primary inline-flex items-center"
          data-testid="add-product-btn"
        >
          <Plus size={16} className="mr-2" /> Add Product
        </button>
      </div>

      {showForm && (
        <div className="bg-white p-6 rounded-lg card-shadow mb-8">
          <h2 className="font-playfair text-xl font-semibold mb-4">{editingId ? 'Edit' : 'Add'} Product</h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block font-manrope text-sm font-medium mb-2">Category</label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value, subcategory_id: '' })}
                  className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope"
                  required
                  data-testid="product-category-select"
                >
                  <option value="">Select Category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block font-manrope text-sm font-medium mb-2">Subcategory</label>
                <select
                  value={formData.subcategory_id}
                  onChange={(e) => setFormData({ ...formData, subcategory_id: e.target.value })}
                  className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope"
                  required
                  disabled={!formData.category_id}
                  data-testid="product-subcategory-select"
                >
                  <option value="">Select Subcategory</option>
                  {filteredSubcategories.map((sub) => (
                    <option key={sub.id} value={sub.id}>{sub.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block font-manrope text-sm font-medium mb-2">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') })}
                  className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope"
                  required
                  data-testid="product-name-input"
                />
              </div>
              <div>
                <label className="block font-manrope text-sm font-medium mb-2">Price (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope"
                  required
                  data-testid="product-price-input"
                />
              </div>
            </div>
            
            <div>
              <label className="block font-manrope text-sm font-medium mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope h-24"
                data-testid="product-description-input"
              />
            </div>

            {/* Image Manager */}
            <div className="border border-[#E5E0D8] rounded-lg p-6 bg-[#F9F7F2]/50">
              <h3 className="font-manrope font-semibold text-lg mb-4 flex items-center gap-2">
                <ImageIcon size={20} className="text-[#D4AF37]" />
                Product Images
              </h3>
              
              {/* Main Image */}
              <div className="mb-6">
                <label className="block font-manrope text-sm font-medium mb-2">Main Image URL</label>
                <div className="flex gap-3">
                  <input
                    type="url"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    className="flex-1 px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope bg-white"
                    placeholder="https://example.com/image.jpg"
                    data-testid="product-image-input"
                  />
                </div>
                {formData.image_url && (
                  <div className="mt-3 relative inline-block">
                    <img 
                      src={formData.image_url} 
                      alt="Main preview" 
                      className="w-32 h-32 object-cover rounded-lg border-2 border-[#D4AF37]"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                    <span className="absolute -top-2 -right-2 bg-[#D4AF37] text-white text-xs px-2 py-1 rounded-full">Main</span>
                  </div>
                )}
              </div>

              {/* Additional Images */}
              <div>
                <label className="block font-manrope text-sm font-medium mb-2">Additional Gallery Images</label>
                <div className="flex gap-3 mb-4">
                  <input
                    type="url"
                    value={newImageUrl}
                    onChange={(e) => setNewImageUrl(e.target.value)}
                    className="flex-1 px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope bg-white"
                    placeholder="Paste image URL and click Add"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addImage())}
                    data-testid="product-gallery-input"
                  />
                  <button 
                    type="button" 
                    onClick={addImage}
                    className="px-6 py-3 bg-[#4A0404] text-white rounded-md font-manrope font-medium hover:bg-[#380303] transition-colors"
                    data-testid="add-gallery-image-btn"
                  >
                    <Plus size={18} />
                  </button>
                </div>

                {/* Image Gallery Grid */}
                {formData.images.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    {formData.images.map((img, index) => (
                      <div 
                        key={index}
                        draggable
                        onDragStart={() => handleDragStart(index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragEnd={handleDragEnd}
                        className={`relative group cursor-move ${draggedIndex === index ? 'opacity-50' : ''}`}
                        data-testid={`gallery-image-${index}`}
                      >
                        <img 
                          src={img} 
                          alt={`Gallery ${index + 1}`}
                          className="w-full aspect-square object-cover rounded-lg border border-[#E5E0D8] group-hover:border-[#D4AF37] transition-colors"
                          onError={(e) => e.target.src = 'https://via.placeholder.com/150?text=Error'}
                        />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex flex-col items-center justify-center gap-2">
                          <button
                            type="button"
                            onClick={() => setAsMainImage(index)}
                            className="text-white text-xs bg-[#D4AF37] px-2 py-1 rounded hover:bg-[#c4a030]"
                            title="Set as main image"
                          >
                            Set Main
                          </button>
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="text-white text-xs bg-[#9B2226] px-2 py-1 rounded hover:bg-[#7d1b1e]"
                            data-testid={`remove-gallery-image-${index}`}
                          >
                            Remove
                          </button>
                        </div>
                        <span className="absolute top-1 left-1 bg-black/60 text-white text-xs px-2 py-0.5 rounded">
                          {index + 1}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {formData.images.length === 0 && (
                  <p className="text-sm text-[#595959] italic">No additional images. Add URLs above to create a gallery.</p>
                )}

                <p className="text-xs text-[#595959] mt-3">
                  💡 Tip: Drag and drop images to reorder. Hover to set as main or remove.
                </p>
              </div>
            </div>

            {/* Other Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block font-manrope text-sm font-medium mb-2">Slug</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope"
                  required
                  data-testid="product-slug-input"
                />
              </div>
              <div className="flex items-center space-x-6 pt-8">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                    className="w-4 h-4"
                    data-testid="product-featured-checkbox"
                  />
                  <span className="font-manrope text-sm">Featured</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4"
                    data-testid="product-active-checkbox"
                  />
                  <span className="font-manrope text-sm">Active</span>
                </label>
              </div>
            </div>

            <div className="flex space-x-4 pt-4">
              <button type="submit" className="btn-primary" data-testid="product-submit-btn">
                {editingId ? 'Update Product' : 'Create Product'}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="btn-outline">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
      
      <div className="bg-white rounded-lg card-shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#F9F7F2]">
            <tr>
              <th className="px-6 py-4 text-left font-manrope text-sm font-semibold text-[#595959]">Name</th>
              <th className="px-6 py-4 text-left font-manrope text-sm font-semibold text-[#595959]">Price</th>
              <th className="px-6 py-4 text-left font-manrope text-sm font-semibold text-[#595959]">Status</th>
              <th className="px-6 py-4 text-left font-manrope text-sm font-semibold text-[#595959]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map((prod) => (
              <tr key={prod.id} className="border-t border-[#E5E0D8]">
                <td className="px-6 py-4 font-manrope">{prod.name}</td>
                <td className="px-6 py-4 font-manrope">₹{prod.price.toLocaleString()}</td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded text-xs font-manrope ${prod.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                    {prod.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <button onClick={() => handleEdit(prod)} className="text-[#4A0404] hover:underline mr-4" data-testid={`edit-product-${prod.id}`}>
                    <Edit2 size={16} />
                  </button>
                  <button onClick={() => handleDelete(prod.id)} className="text-[#9B2226] hover:underline" data-testid={`delete-product-${prod.id}`}>
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {products.length === 0 && (
          <div className="text-center py-12">
            <p className="font-manrope text-[#595959]">No products yet. Click "Add Product" to get started.</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

const AdminAbout = () => {
  const api = useApi();
  const [about, setAbout] = useState({ title: '', content: '', image_url: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAbout();
  }, []);

  const fetchAbout = async () => {
    try {
      const res = await api.get('/about');
      setAbout(res.data);
    } catch (e) {
      console.error("Error fetching about:", e);
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.put('/admin/about', about);
      toast.success('About page updated');
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  return (
    <AdminLayout activeTab="about">
      <h1 className="font-playfair text-3xl font-bold text-[#1A1A1A] mb-8">About Page</h1>
      
      <div className="bg-white p-6 rounded-lg card-shadow">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block font-manrope text-sm font-medium mb-2">Title</label>
            <input
              type="text"
              value={about.title}
              onChange={(e) => setAbout({ ...about, title: e.target.value })}
              className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope"
              data-testid="about-title-input"
            />
          </div>
          <div>
            <label className="block font-manrope text-sm font-medium mb-2">Content</label>
            <textarea
              value={about.content}
              onChange={(e) => setAbout({ ...about, content: e.target.value })}
              className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope h-48"
              data-testid="about-content-input"
            />
          </div>
          <div>
            <label className="block font-manrope text-sm font-medium mb-2">Image URL</label>
            <input
              type="url"
              value={about.image_url || ''}
              onChange={(e) => setAbout({ ...about, image_url: e.target.value })}
              className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope"
              data-testid="about-image-input"
            />
          </div>
          <button type="submit" className="btn-primary" data-testid="about-submit-btn">
            Save Changes
          </button>
        </form>
      </div>
    </AdminLayout>
  );
};

const AdminSettings = () => {
  const api = useApi();
  const { refreshSettings } = useSettings();
  const [settings, setSettings] = useState({ 
    whatsapp_number: '', 
    whatsapp_message: '',
    phone_number: '',
    email: '',
    address: '',
    business_hours: ''
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/settings');
      setSettings(res.data);
    } catch (e) {
      console.error("Error fetching settings:", e);
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.put('/admin/settings', settings);
      toast.success('Settings updated');
      refreshSettings(); // Refresh global settings context
    } catch (e) {
      toast.error('Failed to update settings');
    }
  };

  return (
    <AdminLayout activeTab="settings">
      <h1 className="font-playfair text-3xl font-bold text-[#1A1A1A] mb-8">Settings</h1>
      
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Contact Information */}
        <div className="bg-white p-6 rounded-lg card-shadow">
          <h2 className="font-playfair text-xl font-semibold mb-6 flex items-center gap-2">
            <MapPin size={20} className="text-[#D4AF37]" />
            Contact Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block font-manrope text-sm font-medium mb-2">Phone Number</label>
              <input
                type="tel"
                value={settings.phone_number || ''}
                onChange={(e) => setSettings({ ...settings, phone_number: e.target.value })}
                className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope"
                placeholder="+91 9876543210"
                data-testid="settings-phone-number"
              />
            </div>
            <div>
              <label className="block font-manrope text-sm font-medium mb-2">Email Address</label>
              <input
                type="email"
                value={settings.email || ''}
                onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope"
                placeholder="info@ltcards.in"
                data-testid="settings-email"
              />
            </div>
          </div>
          <div className="mt-6">
            <label className="block font-manrope text-sm font-medium mb-2">Business Address</label>
            <textarea
              value={settings.address || ''}
              onChange={(e) => setSettings({ ...settings, address: e.target.value })}
              className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope h-24"
              placeholder="Shop No. 1 & 2, Mahaveer Market, Near Mehra Cinema, Aminabad, Gwynne Road, Lucknow, UP - 226018"
              data-testid="settings-address"
            />
            <p className="text-sm text-[#595959] mt-1">This will appear in the Contact Us section on the website</p>
          </div>
          <div className="mt-6">
            <label className="block font-manrope text-sm font-medium mb-2">Business Hours</label>
            <textarea
              value={settings.business_hours || ''}
              onChange={(e) => setSettings({ ...settings, business_hours: e.target.value })}
              className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope h-20"
              placeholder="Monday - Saturday: 10:00 AM - 8:00 PM&#10;Sunday: Closed"
              data-testid="settings-business-hours"
            />
            <p className="text-sm text-[#595959] mt-1">Enter each day on a new line</p>
          </div>
        </div>

        {/* WhatsApp Integration */}
        <div className="bg-white p-6 rounded-lg card-shadow">
          <h2 className="font-playfair text-xl font-semibold mb-6 flex items-center gap-2">
            <MessageCircle size={20} className="text-[#25D366]" />
            WhatsApp Integration
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block font-manrope text-sm font-medium mb-2">WhatsApp Number</label>
              <input
                type="tel"
                value={settings.whatsapp_number || ''}
                onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })}
                className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope"
                placeholder="+91 9876543210"
                data-testid="settings-whatsapp-number"
              />
              <p className="text-sm text-[#595959] mt-1">Enter with country code (e.g., +91 for India)</p>
            </div>
            <div>
              <label className="block font-manrope text-sm font-medium mb-2">Default WhatsApp Message</label>
              <textarea
                value={settings.whatsapp_message || ''}
                onChange={(e) => setSettings({ ...settings, whatsapp_message: e.target.value })}
                className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope h-[88px]"
                placeholder="Hi! I'm interested in your products."
                data-testid="settings-whatsapp-message"
              />
            </div>
          </div>
        </div>

        <button type="submit" className="btn-primary" data-testid="settings-submit-btn">
          Save All Settings
        </button>
      </form>
      
      {/* Preview */}
      <div className="bg-[#F9F7F2] p-6 rounded-lg mt-8">
        <h3 className="font-manrope font-semibold mb-4">Preview - Contact Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
          <div>
            <p className="text-[#595959] mb-1"><strong>Phone:</strong> {settings.phone_number || 'Not set'}</p>
            <p className="text-[#595959] mb-1"><strong>Email:</strong> {settings.email || 'Not set'}</p>
            <p className="text-[#595959] mb-1"><strong>WhatsApp:</strong> {settings.whatsapp_number || 'Not set'}</p>
          </div>
          <div>
            <p className="text-[#595959] mb-1"><strong>Address:</strong></p>
            <p className="text-[#595959] whitespace-pre-line">{settings.address || 'Not set'}</p>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

// ============ ADMIN TESTIMONIALS ============

const AdminTestimonials = () => {
  const api = useApi();
  const [testimonials, setTestimonials] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '', text: '', rating: 5, location: '', image_url: '', is_active: true, order: 0
  });

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const res = await api.get('/testimonials?active_only=false');
      setTestimonials(res.data);
    } catch (e) {
      toast.error("Failed to load testimonials");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/admin/testimonials/${editingId}`, formData);
        toast.success('Testimonial updated');
      } else {
        await api.post('/admin/testimonials', formData);
        toast.success('Testimonial created');
      }
      setShowForm(false);
      setEditingId(null);
      setFormData({ name: '', text: '', rating: 5, location: '', image_url: '', is_active: true, order: 0 });
      fetchTestimonials();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (item) => {
    setFormData({
      name: item.name,
      text: item.text,
      rating: item.rating,
      location: item.location || '',
      image_url: item.image_url || '',
      is_active: item.is_active,
      order: item.order,
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    try {
      await api.delete(`/admin/testimonials/${id}`);
      toast.success('Testimonial deleted');
      fetchTestimonials();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  return (
    <AdminLayout activeTab="testimonials">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-playfair text-3xl font-bold text-[#1A1A1A]">Testimonials</h1>
        <button 
          onClick={() => { setShowForm(true); setEditingId(null); setFormData({ name: '', text: '', rating: 5, location: '', image_url: '', is_active: true, order: 0 }); }} 
          className="btn-primary inline-flex items-center"
          data-testid="add-testimonial-btn"
        >
          <Plus size={16} className="mr-2" /> Add Testimonial
        </button>
      </div>

      {showForm && (
        <div className="bg-white p-6 rounded-lg card-shadow mb-8">
          <h2 className="font-playfair text-xl font-semibold mb-4">{editingId ? 'Edit' : 'Add'} Testimonial</h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block font-manrope text-sm font-medium mb-2">Customer Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope"
                required
                data-testid="testimonial-name-input"
              />
            </div>
            <div>
              <label className="block font-manrope text-sm font-medium mb-2">Location (optional)</label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope"
                placeholder="e.g., Mumbai, India"
                data-testid="testimonial-location-input"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block font-manrope text-sm font-medium mb-2">Testimonial Text</label>
              <textarea
                value={formData.text}
                onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope h-24"
                required
                data-testid="testimonial-text-input"
              />
            </div>
            <div>
              <label className="block font-manrope text-sm font-medium mb-2">Rating (1-5)</label>
              <div className="flex gap-2">
                {[1,2,3,4,5].map(num => (
                  <button
                    key={num}
                    type="button"
                    onClick={() => setFormData({ ...formData, rating: num })}
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${formData.rating >= num ? 'bg-[#D4AF37] text-white' : 'bg-[#F9F7F2] text-[#595959]'}`}
                  >
                    <Star size={18} fill={formData.rating >= num ? 'white' : 'none'} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block font-manrope text-sm font-medium mb-2">Order</label>
              <input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope"
                data-testid="testimonial-order-input"
              />
            </div>
            <div>
              <label className="block font-manrope text-sm font-medium mb-2">Customer Photo URL (optional)</label>
              <input
                type="url"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope"
                data-testid="testimonial-image-input"
              />
            </div>
            <div className="flex items-center">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <span className="font-manrope text-sm">Active (visible on site)</span>
              </label>
            </div>
            <div className="md:col-span-2 flex space-x-4">
              <button type="submit" className="btn-primary" data-testid="testimonial-submit-btn">
                {editingId ? 'Update' : 'Create'}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="btn-outline">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg card-shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#F9F7F2]">
            <tr>
              <th className="px-6 py-4 text-left font-manrope text-sm font-semibold text-[#595959]">Customer</th>
              <th className="px-6 py-4 text-left font-manrope text-sm font-semibold text-[#595959]">Review</th>
              <th className="px-6 py-4 text-left font-manrope text-sm font-semibold text-[#595959]">Rating</th>
              <th className="px-6 py-4 text-left font-manrope text-sm font-semibold text-[#595959]">Status</th>
              <th className="px-6 py-4 text-left font-manrope text-sm font-semibold text-[#595959]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {testimonials.map((item) => (
              <tr key={item.id} className="border-t border-[#E5E0D8]">
                <td className="px-6 py-4">
                  <div className="font-manrope font-medium">{item.name}</div>
                  {item.location && <div className="text-xs text-[#595959]">{item.location}</div>}
                </td>
                <td className="px-6 py-4 font-manrope text-sm text-[#595959] max-w-xs truncate">{item.text}</td>
                <td className="px-6 py-4">
                  <div className="flex text-[#D4AF37]">
                    {[...Array(item.rating)].map((_, i) => <Star key={i} size={14} fill="#D4AF37" />)}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded text-xs font-manrope ${item.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                    {item.is_active ? 'Active' : 'Hidden'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <button onClick={() => handleEdit(item)} className="text-[#4A0404] hover:underline mr-4">
                    <Edit2 size={16} />
                  </button>
                  <button onClick={() => handleDelete(item.id)} className="text-[#9B2226] hover:underline">
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {testimonials.length === 0 && (
          <div className="text-center py-12">
            <Quote size={48} className="mx-auto text-[#E5E0D8] mb-4" />
            <p className="font-manrope text-[#595959]">No testimonials yet. Click "Add Testimonial" to get started.</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

// ============ ADMIN PRODUCT REVIEWS ============

const AdminReviews = () => {
  const api = useApi();
  const [reviews, setReviews] = useState([]);
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState('all'); // all, pending, approved

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [reviewsRes, productsRes] = await Promise.all([
        api.get('/admin/reviews', true),
        api.get('/products'),
      ]);
      setReviews(reviewsRes.data);
      setProducts(productsRes.data);
    } catch (e) {
      toast.error("Failed to load reviews");
    }
  };

  const handleApprove = async (id) => {
    try {
      await api.put(`/admin/reviews/${id}/approve`);
      toast.success('Review approved');
      fetchData();
    } catch (e) {
      toast.error('Failed to approve');
    }
  };

  const handleReject = async (id) => {
    try {
      await api.put(`/admin/reviews/${id}/reject`);
      toast.success('Review rejected');
      fetchData();
    } catch (e) {
      toast.error('Failed to reject');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this review permanently?')) return;
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success('Review deleted');
      fetchData();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId);
    return product ? product.name : 'Unknown Product';
  };

  const filteredReviews = reviews.filter(r => {
    if (filter === 'pending') return !r.is_approved;
    if (filter === 'approved') return r.is_approved;
    return true;
  });

  const pendingCount = reviews.filter(r => !r.is_approved).length;

  return (
    <AdminLayout activeTab="reviews">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-playfair text-3xl font-bold text-[#1A1A1A]">Product Reviews</h1>
          {pendingCount > 0 && (
            <p className="font-manrope text-sm text-[#D4AF37] mt-1">{pendingCount} reviews pending approval</p>
          )}
        </div>
        <div className="flex gap-2">
          {['all', 'pending', 'approved'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-full font-manrope text-sm capitalize ${filter === f ? 'bg-[#4A0404] text-white' : 'bg-[#F9F7F2] text-[#595959]'}`}
            >
              {f} {f === 'pending' && pendingCount > 0 && `(${pendingCount})`}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        {filteredReviews.map((review) => (
          <div key={review.id} className={`bg-white p-6 rounded-lg card-shadow ${!review.is_approved ? 'border-l-4 border-[#D4AF37]' : ''}`}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="font-manrope font-semibold">{review.customer_name}</span>
                  <div className="flex text-[#D4AF37]">
                    {[...Array(review.rating)].map((_, i) => <Star key={i} size={14} fill="#D4AF37" />)}
                  </div>
                  <span className={`px-2 py-0.5 rounded text-xs font-manrope ${review.is_approved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                    {review.is_approved ? 'Approved' : 'Pending'}
                  </span>
                </div>
                <p className="text-sm text-[#595959] mb-2">Product: <Link to={`/product/${getProductName(review.product_id).toLowerCase().replace(/\s+/g, '-')}`} className="text-[#4A0404] hover:underline">{getProductName(review.product_id)}</Link></p>
                {review.title && <p className="font-manrope font-medium mb-1">{review.title}</p>}
                <p className="font-manrope text-[#595959]">{review.comment}</p>
                {review.customer_email && <p className="text-xs text-[#595959] mt-2">Email: {review.customer_email}</p>}
              </div>
              <div className="flex gap-2 ml-4">
                {!review.is_approved && (
                  <button onClick={() => handleApprove(review.id)} className="p-2 bg-green-100 text-green-800 rounded-full hover:bg-green-200" title="Approve">
                    <CheckCircle size={18} />
                  </button>
                )}
                {review.is_approved && (
                  <button onClick={() => handleReject(review.id)} className="p-2 bg-yellow-100 text-yellow-800 rounded-full hover:bg-yellow-200" title="Hide">
                    <XCircle size={18} />
                  </button>
                )}
                <button onClick={() => handleDelete(review.id)} className="p-2 bg-red-100 text-red-800 rounded-full hover:bg-red-200" title="Delete">
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredReviews.length === 0 && (
        <div className="bg-white rounded-lg card-shadow p-12 text-center">
          <Star size={48} className="mx-auto text-[#E5E0D8] mb-4" />
          <p className="font-manrope text-[#595959]">
            {filter === 'pending' ? 'No pending reviews' : filter === 'approved' ? 'No approved reviews' : 'No reviews yet. Reviews submitted by customers will appear here.'}
          </p>
        </div>
      )}
    </AdminLayout>
  );
};

// ============ MAIN APP ============

const StorefrontLayout = ({ children, categories }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header categories={categories} />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
};

// ============ BLOG COMPONENTS ============

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const res = await axios.get(`${API}/blog`);
        setPosts(res.data);
      } catch (e) {
        console.error("Error fetching blog posts:", e);
      }
      setLoading(false);
    };
    fetchPosts();
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
      <SEO 
        title="Blog - Wedding Tips, Ideas & Inspiration"
        description="Read our latest articles on wedding invitations, packaging trends, and celebration ideas."
      />
      <div className="text-center mb-12">
        <p className="font-cormorant text-lg text-[#D4AF37] italic mb-2">Our Blog</p>
        <h1 className="font-playfair text-4xl md:text-5xl font-bold text-[#1A1A1A] mb-4">Tips & Inspiration</h1>
        <div className="gold-line" />
        <p className="font-manrope text-[#595959] mt-4 max-w-2xl mx-auto">
          Discover wedding trends, invitation ideas, and expert tips for your special occasions.
        </p>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-pulse font-manrope text-[#595959]">Loading posts...</div>
        </div>
      ) : posts.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {posts.map((post) => (
            <Link to={`/blog/${post.slug}`} key={post.id} className="group">
              <div className="bg-white rounded-lg overflow-hidden card-shadow hover:shadow-xl transition-shadow">
                {post.image_url && (
                  <div className="aspect-video overflow-hidden">
                    <img 
                      src={post.image_url} 
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center gap-4 text-sm text-[#595959] mb-3">
                    <span className="flex items-center gap-1">
                      <Calendar size={14} />
                      {new Date(post.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                    <span className="flex items-center gap-1">
                      <User size={14} />
                      {post.author}
                    </span>
                  </div>
                  <h2 className="font-playfair text-xl font-semibold text-[#1A1A1A] mb-2 group-hover:text-[#4A0404] transition-colors">
                    {post.title}
                  </h2>
                  <p className="font-manrope text-[#595959] text-sm line-clamp-2">
                    {post.excerpt}
                  </p>
                  {post.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {post.tags.slice(0, 3).map((tag, i) => (
                        <span key={i} className="px-2 py-1 bg-[#F9F7F2] text-[#595959] text-xs rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 bg-[#F9F7F2] rounded-lg">
          <BookOpen size={48} className="mx-auto text-[#D4AF37] mb-4" />
          <h3 className="font-playfair text-2xl text-[#1A1A1A] mb-2">Coming Soon</h3>
          <p className="font-manrope text-[#595959]">We're working on exciting content for you. Check back soon!</p>
        </div>
      )}
    </div>
  );
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const res = await axios.get(`${API}/blog/${slug}`);
        setPost(res.data);
      } catch (e) {
        console.error("Error fetching blog post:", e);
      }
      setLoading(false);
    };
    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse font-manrope text-[#595959]">Loading...</div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-playfair text-2xl mb-4">Post not found</h2>
          <Link to="/blog" className="btn-primary">Back to Blog</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-12 py-16">
      <SEO 
        title={post.title}
        description={post.excerpt}
      />
      <Link to="/blog" className="inline-flex items-center gap-2 text-[#4A0404] hover:text-[#D4AF37] font-manrope mb-8 transition-colors">
        <ChevronLeft size={18} /> Back to Blog
      </Link>
      
      {post.image_url && (
        <div className="aspect-video rounded-lg overflow-hidden mb-8">
          <img src={post.image_url} alt={post.title} className="w-full h-full object-cover" />
        </div>
      )}
      
      <div className="flex items-center gap-4 text-sm text-[#595959] mb-4">
        <span className="flex items-center gap-1">
          <Calendar size={14} />
          {new Date(post.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
        </span>
        <span className="flex items-center gap-1">
          <User size={14} />
          {post.author}
        </span>
      </div>
      
      <h1 className="font-playfair text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-6">{post.title}</h1>
      
      {post.tags?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-8">
          {post.tags.map((tag, i) => (
            <span key={i} className="px-3 py-1 bg-[#F9F7F2] text-[#595959] text-sm rounded-full">
              {tag}
            </span>
          ))}
        </div>
      )}
      
      <div className="prose prose-lg max-w-none font-manrope text-[#595959] leading-relaxed whitespace-pre-line">
        {post.content}
      </div>
    </div>
  );
};

const AdminBlog = () => {
  const api = useApi();
  const [posts, setPosts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '', slug: '', excerpt: '', content: '', image_url: '', author: 'Admin', tags: '', is_published: true
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const res = await api.get('/blog?published_only=false', true);
      setPosts(res.data);
    } catch (e) {
      toast.error("Failed to load blog posts");
    }
  };

  const generateSlug = (title) => {
    return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean)
    };
    
    try {
      if (editingId) {
        await api.put(`/admin/blog/${editingId}`, data);
        toast.success('Blog post updated');
      } else {
        await api.post('/admin/blog', data);
        toast.success('Blog post created');
      }
      resetForm();
      fetchPosts();
    } catch (e) {
      toast.error('Failed to save blog post');
    }
  };

  const handleEdit = (post) => {
    setFormData({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      image_url: post.image_url || '',
      author: post.author,
      tags: post.tags?.join(', ') || '',
      is_published: post.is_published
    });
    setEditingId(post.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this blog post?')) return;
    try {
      await api.delete(`/admin/blog/${id}`);
      toast.success('Blog post deleted');
      fetchPosts();
    } catch (e) {
      toast.error('Failed to delete blog post');
    }
  };

  const resetForm = () => {
    setFormData({ title: '', slug: '', excerpt: '', content: '', image_url: '', author: 'Admin', tags: '', is_published: true });
    setEditingId(null);
    setShowForm(false);
  };

  return (
    <AdminLayout activeTab="blog">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-playfair text-3xl font-bold text-[#1A1A1A]">Blog Posts</h1>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary" data-testid="add-blog-btn">
          <Plus size={18} className="inline mr-2" /> New Post
        </button>
      </div>

      {showForm && (
        <div className="bg-white p-6 rounded-lg card-shadow mb-8">
          <h2 className="font-playfair text-xl font-semibold mb-4">{editingId ? 'Edit Post' : 'New Post'}</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block font-manrope text-sm font-medium mb-2">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value, slug: generateSlug(e.target.value) })}
                  className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope"
                  required
                />
              </div>
              <div>
                <label className="block font-manrope text-sm font-medium mb-2">Slug</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block font-manrope text-sm font-medium mb-2">Excerpt (Short Description)</label>
              <textarea
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope h-20"
                required
              />
            </div>
            <div>
              <label className="block font-manrope text-sm font-medium mb-2">Content</label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope h-48"
                required
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block font-manrope text-sm font-medium mb-2">Featured Image URL</label>
                <input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope"
                />
              </div>
              <div>
                <label className="block font-manrope text-sm font-medium mb-2">Tags (comma separated)</label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md font-manrope"
                  placeholder="wedding, invitations, tips"
                />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_published}
                  onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                  className="w-4 h-4"
                />
                <span className="font-manrope text-sm">Published</span>
              </label>
            </div>
            <div className="flex gap-4">
              <button type="submit" className="btn-primary">{editingId ? 'Update' : 'Create'} Post</button>
              <button type="button" onClick={resetForm} className="btn-outline">Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg card-shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#F9F7F2]">
            <tr>
              <th className="px-6 py-4 text-left font-manrope text-sm font-semibold text-[#595959]">Title</th>
              <th className="px-6 py-4 text-left font-manrope text-sm font-semibold text-[#595959]">Status</th>
              <th className="px-6 py-4 text-left font-manrope text-sm font-semibold text-[#595959]">Date</th>
              <th className="px-6 py-4 text-left font-manrope text-sm font-semibold text-[#595959]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {posts.map((post) => (
              <tr key={post.id} className="border-t border-[#E5E0D8]">
                <td className="px-6 py-4">
                  <p className="font-manrope font-medium">{post.title}</p>
                  <p className="font-manrope text-sm text-[#595959]">{post.excerpt?.substring(0, 60)}...</p>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs ${post.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                    {post.is_published ? 'Published' : 'Draft'}
                  </span>
                </td>
                <td className="px-6 py-4 font-manrope text-sm text-[#595959]">
                  {new Date(post.created_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4">
                  <div className="flex gap-3">
                    <button onClick={() => handleEdit(post)} className="text-[#4A0404] hover:underline">
                      <Edit2 size={16} />
                    </button>
                    <button onClick={() => handleDelete(post.id)} className="text-red-500 hover:underline">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {posts.length === 0 && (
          <div className="text-center py-12">
            <p className="font-manrope text-[#595959]">No blog posts yet. Create your first post!</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

function App() {
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${API}/categories`);
        setCategories(res.data);
      } catch (e) {
        console.error("Error fetching categories:", e);
      }
    };
    fetchCategories();
    
    // Setup admin user on first load
    axios.post(`${API}/setup/admin`).catch(() => {});
    
    // Disable right-click on images
    const handleContextMenu = (e) => {
      if (e.target.tagName === 'IMG') {
        e.preventDefault();
        return false;
      }
    };
    
    // Disable drag on images
    const handleDragStart = (e) => {
      if (e.target.tagName === 'IMG') {
        e.preventDefault();
        return false;
      }
    };
    
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('dragstart', handleDragStart);
    
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('dragstart', handleDragStart);
    };
  }, []);

  return (
    <AuthProvider>
      <SettingsProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Toaster position="top-right" richColors />
          <FloatingWhatsApp />
          <Routes>
              {/* Admin Routes */}
              <Route path="/admin" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/categories" element={<AdminCategories />} />
              <Route path="/admin/subcategories" element={<AdminSubcategories />} />
              <Route path="/admin/products" element={<AdminProducts />} />
              <Route path="/admin/testimonials" element={<AdminTestimonials />} />
              <Route path="/admin/reviews" element={<AdminReviews />} />
              <Route path="/admin/about" element={<AdminAbout />} />
              <Route path="/admin/settings" element={<AdminSettings />} />
              <Route path="/admin/blog" element={<AdminBlog />} />
              
              {/* Storefront Routes */}
              <Route path="/" element={<StorefrontLayout categories={categories}><HomePage categories={categories} /></StorefrontLayout>} />
              <Route path="/about" element={<StorefrontLayout categories={categories}><AboutPage /></StorefrontLayout>} />
              <Route path="/category/:slug" element={<StorefrontLayout categories={categories}><CategoryPage categories={categories} /></StorefrontLayout>} />
              <Route path="/subcategory/:slug" element={<StorefrontLayout categories={categories}><SubcategoryPage categories={categories} /></StorefrontLayout>} />
              <Route path="/product/:slug" element={<StorefrontLayout categories={categories}><ProductPage /></StorefrontLayout>} />
              <Route path="/blog" element={<StorefrontLayout categories={categories}><BlogPage /></StorefrontLayout>} />
              <Route path="/blog/:slug" element={<StorefrontLayout categories={categories}><BlogPostPage /></StorefrontLayout>} />
            </Routes>
          </BrowserRouter>
        </SettingsProvider>
      </AuthProvider>
  );
}

export default App;
